import NewTripCard from '@pages/FlightSearchResult/components/NewTripCard';
import TripCard from '@pages/FlightSearchResult/components/TripCard';
import { isBaggageFilterOn } from '@pages/FlightSearchResult/constants';
import { providerCodesForBaggageFilter } from '@pages/FlightSearchResult/constants';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchSort from '@pages/FlightSearchResult/hooks/useFlightSearchSort';
import { GenzoTrackApplyFilterActionEventType } from '@pages/FlightSearchResult/types';
import type { GroupedTripViewModel, TripViewModel } from 'flights/tripViewModel';
import { uniq } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  AutoSizer,
  List,
  WindowScroller,
  type Index,
  type ListRowProps,
  type Size,
  type WindowScrollerChildProps,
} from 'react-virtualized';

import { getRealAdPosition } from '@components/flights/FlightSearchResultContentAds/helper';

import { TEST_FEATURES_PARAM, MOCK_BAGGAGE_FILTER } from '@constants/flight';

import { useFlightContext } from '@context/FlightContext';

import {
  getFlightSearchResultDataDD,
  getHeightOfHighlightTags,
  hasBnplPaymentOptionForTripCardDisplay,
} from '@helpers/flight';
import { sortFlightFares } from '@helpers/flightSort';

import { FlightSearchResultImpressionLogging } from '@hooks/useFlightSearchResultImpressionLogging';
import { DD, SABRE_FARE_DD } from '@hooks/useFlightSearchResultsDatadog';
import { useViewportSize } from '@hooks/useViewportSize';

import { getTranslationsState } from '@redux/selectors';

import { AdPositionConfig } from '@wegoTypes/adPositionConfig';

import styles from './FlightSearchResultList.module.scss';

type FlightSearchResultListProps = {
  gtmAuth: string;
  gtmPreview: string;
  impressionLogging: FlightSearchResultImpressionLogging;
  onTripViewModelSelect: (tripViewModel: TripViewModel) => void;
  onTripCardMouseHover: (shown: boolean) => void;
  genzoTrackApplyFilterSearchView: () => void;
  genzoTrackApplyFilterActionEvent: GenzoTrackApplyFilterActionEventType;
  wegoAnalyticsClientId?: string;
  wegoAnalyticsClientSessionId?: string;
  availablePaymentMethods: HomepageFlightsPaymentMethod[];
  nativeTopAdHeight?: number;
  nativeMiddleAdHeight?: number;
  nativeBottomAdHeight?: number;
  leaderboardAdsAndInlineAdsConfig: AdPositionConfig[];
  setVisibleTripCardForUngroupedTrips: React.Dispatch<
    React.SetStateAction<TripViewModelDataForGenzo[]>
  >;
  setVisibleTripCardForGroupedTrips: React.Dispatch<
    React.SetStateAction<TripViewModelDataForGenzo[]>
  >;
  topLeaderboardAdsHeight: number;
  middleLeaderboardAdsHeight: number;
  bottomLeaderboardAdsHeight: number;
};

const placeholderTripViewModel: TripViewModel = {
  fares: [],
  filteredFares: [],
  legs: [],
  tripId: '',
  isPlaceholder: true,
  placeholderStyle: {
    height: 30,
  },
};

const FlightSearchResultList: React.FC<FlightSearchResultListProps> = ({
  gtmAuth,
  gtmPreview,
  impressionLogging,
  onTripViewModelSelect,
  onTripCardMouseHover,
  genzoTrackApplyFilterSearchView,
  genzoTrackApplyFilterActionEvent,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
  availablePaymentMethods,
  nativeBottomAdHeight,
  nativeMiddleAdHeight,
  nativeTopAdHeight,
  leaderboardAdsAndInlineAdsConfig,
  setVisibleTripCardForUngroupedTrips,
  setVisibleTripCardForGroupedTrips,
  bottomLeaderboardAdsHeight,
  middleLeaderboardAdsHeight,
  topLeaderboardAdsHeight,
}) => {
  const { isFlightSearch2AndNotMulticity } = useFlightContext();

  const listRef = useRef<List>(null);

  const { width } = useViewportSize();
  const isTablet = width < 1024;

  const translations = useSelector(getTranslationsState);

  const {
    groupedTripViewModelsDetail,
    sortedAndFilteredTripViewModels,
    expandedGroupKeys,
    setExpandedGroupKeys,
    sortedAndFilteredTripViewModelsWithoutPlaceholder,
    groupedTripViewModelDetailWithoutPlaceholder,
    sortedAndFilteredTripViewModelsForRecommendedSortFlight2,
    sortedAndFilteredTripViewModelsForPerLegSearch,
    isLoading,
    finalTripViewModelsToShowBasedOnSortType,
    offerEngineIntegrationAbTestVariant,
  } = useFlightSearchResults();

  const { sortType, sortOrder } = useFlightSearchSort();

  const { isPerLegSearch, isPLSDepartLegSelection, isPLSChangeLegLoading } = useFlightPLS();

  const tripViewModelsToDisplayForNonPriceSort = useMemo(() => {
    // Add a placeholder at the end of the list to make room for the tooltip of the last actual card
    if (isFlightSearch2AndNotMulticity && sortType === 'score') {
      return sortedAndFilteredTripViewModelsForRecommendedSortFlight2.concat(
        placeholderTripViewModel,
      );
    }

    if (isPerLegSearch) {
      return sortedAndFilteredTripViewModelsForPerLegSearch.concat(placeholderTripViewModel);
    }

    return sortedAndFilteredTripViewModels;
  }, [
    isPerLegSearch,
    isFlightSearch2AndNotMulticity,
    sortType,
    sortedAndFilteredTripViewModels,
    sortedAndFilteredTripViewModelsForPerLegSearch,
    sortedAndFilteredTripViewModelsForRecommendedSortFlight2,
  ]);

  const sortedAndFilteredTripViewModelsForPLSRecommendedSortWithoutGroupHeaderPlaceholder =
    useMemo(() => {
      return sortedAndFilteredTripViewModelsForRecommendedSortFlight2.filter(
        (trip) => !trip.headerType,
      );
    }, [sortedAndFilteredTripViewModelsForRecommendedSortFlight2]);

  const groupedTripViewModelsDetailToShow = useMemo(() => {
    // Add a placeholder at the end of the list to make room for the tooltip of the last actual card
    if (isFlightSearch2AndNotMulticity) {
      const placeholderTripViewModelGroup: GroupedTripViewModel = {
        key: 'placeholder',
        index: 0,
        maxLength: 0,
        tripViewModel: placeholderTripViewModel,
      };

      return groupedTripViewModelsDetail.concat(placeholderTripViewModelGroup);
    }

    return groupedTripViewModelsDetail;
  }, [groupedTripViewModelsDetail, isFlightSearch2AndNotMulticity]);

  const [scrollToIndex, setScrollToIndex] = useState<number>();
  const [visibleIndexRange, setVisibleIndexRange] = useState<{
    startIndex: number;
    stopIndex: number;
  }>();

  const [searchParams] = useSearchParams();

  const isShowBaggageFilter = useMemo(() => {
    const testFeaturesSearchParam = searchParams.get(TEST_FEATURES_PARAM);

    return (
      testFeaturesSearchParam && testFeaturesSearchParam.split(',').includes(MOCK_BAGGAGE_FILTER)
    );
  }, [searchParams]);

  const dataDD = useMemo(() => {
    const filterSearchParams = new URLSearchParams(searchParams);
    filterSearchParams.delete('sort');
    filterSearchParams.delete('order');

    return {
      'data-dd': DD,
      'data-dd-sort': `${sortType}-${sortOrder}`,
      'data-dd-filter': filterSearchParams.toString(),
      'data-dd-offer-engine-integration-ab-test-variant': offerEngineIntegrationAbTestVariant,
    };
  }, [searchParams, sortOrder, sortType, offerEngineIntegrationAbTestVariant]);

  const sabreFareDataDD = useMemo(() => {
    return {
      'data-dd': SABRE_FARE_DD,
      'data-dd-offer-engine-integration-ab-test-variant': offerEngineIntegrationAbTestVariant,
    };
  }, [offerEngineIntegrationAbTestVariant]);

  const handleSimilarOptionsClick = useCallback(
    (groupKey: string): void => {
      setExpandedGroupKeys((prev) => {
        if (prev.includes(groupKey)) {
          return prev.filter((key) => key !== groupKey);
        } else {
          return [...prev, groupKey];
        }
      });
      setScrollToIndex(
        groupedTripViewModelsDetailToShow.findIndex(
          (groupedTripViewModel) => groupedTripViewModel.key === groupKey,
        ),
      );
      impressionLogging.handleSimilarOptionClick();
    },
    [groupedTripViewModelsDetailToShow],
  );

  const getHeightOfBaggageInfo = useCallback(
    (tripViewModel: TripViewModel) => {
      const isBaggageFarePresent = tripViewModel.fares.some((fare) => {
        return providerCodesForBaggageFilter.includes(fare.fare.providerCode);
      });

      return isBaggageFarePresent ? 24 : 0;
    },
    [providerCodesForBaggageFilter],
  );

  const getRowHeightSortedAndFilteredTripViewModel = useCallback<({ index }: Index) => number>(
    ({ index }: Index) => {
      if (
        tripViewModelsToDisplayForNonPriceSort[index].placeholderStyle?.height &&
        typeof tripViewModelsToDisplayForNonPriceSort[index].placeholderStyle?.height === 'number'
      ) {
        return tripViewModelsToDisplayForNonPriceSort[index].placeholderStyle?.height as number;
      }

      if (tripViewModelsToDisplayForNonPriceSort[index].headerType) {
        return 64;
      }

      if (isFlightSearch2AndNotMulticity) {
        const baggageInfoHeight = isTablet && isBaggageFilterOn ? 18 : 0;
        const sponsoredAddedHeight = tripViewModelsToDisplayForNonPriceSort[index].sponsored
          ? 12
          : 0;

        return 80 + Math.max(baggageInfoHeight, sponsoredAddedHeight) + 8;
      } else {
        /**
         * Magic Numbers for computing height of a trip card:
         *
         * Height of a one way card = 150
         * Top and bottom padding and border - 24 + 24 + 2 = 50px
         * Height of each leg = 75px
         * Space between each leg = 10px
         * Space between every trip card = 8px
         *
         * Height of the sponsored ad banner = 56px
         * height of the bnpl tag = 26px
         */
        const numberOfLegs = tripViewModelsToDisplayForNonPriceSort[index].legs.length;

        const bnplTag = hasBnplPaymentOptionForTripCardDisplay(
          availablePaymentMethods,
          tripViewModelsToDisplayForNonPriceSort[index].filteredFares.map(
            (fareViewModel) => fareViewModel.fare,
          ),
        );

        const highlightTags = uniq(tripViewModelsToDisplayForNonPriceSort[index].tripCardTags);

        const highlightTagHeight = getHeightOfHighlightTags(highlightTags);

        const heightOfTags = (bnplTag ? 26 : 0) + highlightTagHeight - 36;

        const heightOfBaggageInfo = isShowBaggageFilter
          ? getHeightOfBaggageInfo(tripViewModelsToDisplayForNonPriceSort[index])
          : 0;

        const heightOfTripDetails =
          numberOfLegs === 1
            ? 152 + heightOfBaggageInfo + (heightOfTags > 0 ? heightOfTags : 0)
            : 50 + 75 * numberOfLegs + 10 * (numberOfLegs - 1);
        const spaceBelowCard = 8;

        const sponsoredBannerHeight = tripViewModelsToDisplayForNonPriceSort[index].sponsored
          ? 56
          : 0;

        if (tripViewModelsToDisplayForNonPriceSort[index].isPlaceholder) {
          if (!tripViewModelsToDisplayForNonPriceSort[index].isAdVisible) {
            return 0;
          }

          // 90 is the height of the ad
          // 8 is the bottom padding
          const adId = tripViewModelsToDisplayForNonPriceSort[index].id;
          if (adId === 'top_leaderboard' && nativeTopAdHeight) {
            return nativeTopAdHeight + 8;
          }
          if (adId === 'middle_leaderboard' && nativeMiddleAdHeight) {
            return nativeMiddleAdHeight + 8;
          }
          if (adId === 'bottom_leaderboard' && nativeBottomAdHeight) {
            return nativeBottomAdHeight + 8;
          }
          return 90 + 8;
        }

        return heightOfTripDetails + spaceBelowCard + sponsoredBannerHeight;
      }
    },
    [
      availablePaymentMethods,
      isFlightSearch2AndNotMulticity,
      isTablet,
      nativeBottomAdHeight,
      nativeMiddleAdHeight,
      nativeTopAdHeight,
      tripViewModelsToDisplayForNonPriceSort,
      isShowBaggageFilter,
      getHeightOfBaggageInfo,
    ],
  );

  const totalRowHeightForNewTripCardForNonPriceSort = useMemo(() => {
    return tripViewModelsToDisplayForNonPriceSort.reduce((accumulator, tripViewModel, index) => {
      return accumulator + getRowHeightSortedAndFilteredTripViewModel({ index });
    }, 0);
  }, [getRowHeightSortedAndFilteredTripViewModel, tripViewModelsToDisplayForNonPriceSort]);

  const getRowHeightForGroupedTripViewModel = useCallback<({ index }: Index) => number>(
    ({ index }: Index) => {
      if (
        groupedTripViewModelsDetailToShow[index].tripViewModel.placeholderStyle?.height &&
        typeof groupedTripViewModelsDetailToShow[index].tripViewModel.placeholderStyle?.height ===
          'number'
      ) {
        return groupedTripViewModelsDetailToShow[index].tripViewModel.placeholderStyle
          ?.height as number;
      }

      /**
       * Magic Numbers for computing height of a trip card:
       *
       * padding and border - 24 + 24 + 2 = 50px
       * Bottom Border = 1px
       * Height of each leg = 75px
       * Space between each leg = 10px
       * Height of similar options = 32px
       * Space between every trip card = 8px (No space between cards in a group)
       *
       * Height of the sponsored ad banner = 56px
       * height of the bnpl tag = 26px
       */

      const groupKey = groupedTripViewModelsDetailToShow[index].key;
      const maxLength = groupedTripViewModelsDetailToShow[index].maxLength;
      const numberOfLegs = groupedTripViewModelsDetailToShow[index].tripViewModel.legs.length;
      const tripIndex = groupedTripViewModelsDetailToShow[index].index;

      if (isFlightSearch2AndNotMulticity) {
        const baggageInfoHeight = isBaggageFilterOn && isTablet ? 18 : 0;
        const sponsoredAddedHeight = groupedTripViewModelsDetailToShow[index]?.tripViewModel
          .sponsored
          ? 12
          : 0;

        const heightOfSimilarOptionsForFlight2 =
          maxLength > 1
            ? expandedGroupKeys.includes(groupKey)
              ? tripIndex === maxLength - 1
                ? 32
                : 0
              : 32
            : 0;

        return (
          80 +
          heightOfSimilarOptionsForFlight2 +
          Math.max(baggageInfoHeight, sponsoredAddedHeight) +
          (!expandedGroupKeys.includes(groupKey) || tripIndex === maxLength - 1 ? 8 : 0)
        );
      }

      const heightOfBaggageInfo = isShowBaggageFilter
        ? getHeightOfBaggageInfo(groupedTripViewModelsDetailToShow[index].tripViewModel)
        : 0;

      const indexOfTripCardsFollowedByLeaderBoards: number[] = leaderboardAdsAndInlineAdsConfig
        .filter((ad) => ad.visible)
        .map((ad) => getRealAdPosition(ad, leaderboardAdsAndInlineAdsConfig) - 1);

      const bnplTag = hasBnplPaymentOptionForTripCardDisplay(
        availablePaymentMethods,
        groupedTripViewModelsDetailToShow[index].tripViewModel.filteredFares.map(
          (fareViewModel) => fareViewModel.fare,
        ),
      );

      const highlightTags = uniq(
        groupedTripViewModelsDetailToShow[index].tripViewModel.tripCardTags,
      );

      const highlightTagHeight = getHeightOfHighlightTags(highlightTags);

      const heightOfTags = (bnplTag ? 26 : 0) + highlightTagHeight - 36;

      const heightOfTripDetails =
        numberOfLegs === 1
          ? 152 + heightOfBaggageInfo + (heightOfTags > 0 ? heightOfTags : 0)
          : 50 + 75 * numberOfLegs + 10 * (numberOfLegs - 1);
      const heightOfSimilarOptions =
        maxLength > 1
          ? expandedGroupKeys.includes(groupKey)
            ? tripIndex === 0 || tripIndex === maxLength - 1
              ? 32
              : 0
            : 32
          : 0;
      const spaceBelowCard =
        maxLength <= 1 ||
        !expandedGroupKeys.includes(groupKey) ||
        tripIndex === maxLength - 1 ||
        (expandedGroupKeys.length > 0 && indexOfTripCardsFollowedByLeaderBoards.includes(index))
          ? 8
          : 0;

      if (groupedTripViewModelsDetailToShow[index].tripViewModel.isPlaceholder) {
        if (!groupedTripViewModelsDetailToShow[index].tripViewModel.isAdVisible) {
          return 0;
        }

        const adId = groupedTripViewModelsDetailToShow[index].tripViewModel.id;
        if (adId === 'top_leaderboard' && nativeTopAdHeight) {
          return nativeTopAdHeight + 8;
        }
        if (adId === 'middle_leaderboard' && nativeMiddleAdHeight) {
          return nativeMiddleAdHeight + 8;
        }
        if (adId === 'bottom_leaderboard' && nativeBottomAdHeight) {
          return nativeBottomAdHeight + 8;
        }
        return 90 + 8;
      }

      const sponsoredBannerHeight = groupedTripViewModelsDetailToShow[index]?.tripViewModel
        .sponsored
        ? 56
        : 0;

      return heightOfTripDetails + heightOfSimilarOptions + spaceBelowCard + sponsoredBannerHeight;
    },
    [
      availablePaymentMethods,
      expandedGroupKeys,
      groupedTripViewModelsDetailToShow,
      isFlightSearch2AndNotMulticity,
      isTablet,
      leaderboardAdsAndInlineAdsConfig,
      nativeBottomAdHeight,
      nativeMiddleAdHeight,
      nativeTopAdHeight,
      isShowBaggageFilter,
      getHeightOfBaggageInfo,
    ],
  );

  const totalRowHeightForNewTripCardForPriceSort = useMemo(() => {
    return groupedTripViewModelsDetailToShow.reduce((accumulator, groupedTripViewModel, index) => {
      return accumulator + getRowHeightForGroupedTripViewModel({ index });
    }, 0);
  }, [getRowHeightForGroupedTripViewModel, groupedTripViewModelsDetailToShow]);

  const sortedAndFilteredTripViewModelsRowRenderer = useCallback<
    ({ key, index, isVisible, style }: ListRowProps) => JSX.Element
  >(
    ({ key, index, style }: ListRowProps) => {
      const originalTripViewModel: TripViewModel = tripViewModelsToDisplayForNonPriceSort[index];
      const tripViewModel: TripViewModel =
        isPerLegSearch && !isPLSDepartLegSelection
          ? { ...originalTripViewModel, legs: originalTripViewModel.legs.slice(1) }
          : originalTripViewModel;

      // Find the trip index without the recommend sort group header placeholder
      const indexWithoutHeaderPlaceholderForRecommendedSortPLS =
        sortedAndFilteredTripViewModelsForPLSRecommendedSortWithoutGroupHeaderPlaceholder.findIndex(
          (trip) => trip.tripId === originalTripViewModel.tripId,
        );

      if (originalTripViewModel.headerType) {
        const plsOtherDirectFlightsGroupTitle = isPLSDepartLegSelection
          ? translations.other_departing_direct_flights_group_title
          : translations.Other_returning_direct_flights_group_title;
        const otherDirectFlightsGroupTitle = isPerLegSearch
          ? plsOtherDirectFlightsGroupTitle
          : translations.Other_direct_flights_group_title;
        const plsOtherFlightsGroupTitle = isPLSDepartLegSelection
          ? translations.Other_departing_flights_group_title
          : translations.Other_returning_flights_group_title;
        const otherFlightsGroupTitle = isPerLegSearch
          ? plsOtherFlightsGroupTitle
          : translations.Other_flights_group_title;

        return (
          <div className={styles.headerBar} key={key} style={style}>
            <div className={styles.header}>
              <div className={styles.mainText}>
                {originalTripViewModel.headerType === 'direct'
                  ? otherDirectFlightsGroupTitle
                  : otherFlightsGroupTitle}
              </div>
              <div>
                {originalTripViewModel.headerType === 'direct'
                  ? translations.Other_direct_flights_group_subtitle
                  : translations.Recommended_flights_group_subtitle}
              </div>
            </div>
          </div>
        );
      }

      if (originalTripViewModel.isPlaceholder) {
        return (
          <div
            key={key}
            style={{ ...style, ...originalTripViewModel.placeholderStyle }}
            id={`placeholder-${originalTripViewModel.id}`}
          />
        );
      }

      return (
        <div
          key={key}
          style={style}
          {...getFlightSearchResultDataDD({ tripViewModel, index, dataDD, sabreFareDataDD })}
        >
          {isFlightSearch2AndNotMulticity ? (
            <NewTripCard
              gtmAuth={gtmAuth}
              gtmPreview={gtmPreview}
              impressionLogging={impressionLogging}
              isBaggageFilterOn={isBaggageFilterOn}
              similarOptionsCount={0}
              tripViewModel={tripViewModel}
              genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
              genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
              wegoAnalyticsClientId={wegoAnalyticsClientId}
              wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
              tripCardIndex={
                sortType === 'score' ? indexWithoutHeaderPlaceholderForRecommendedSortPLS : index
              }
              onTripViewModelSelect={() => onTripViewModelSelect(originalTripViewModel)}
            />
          ) : (
            <TripCard
              gtmAuth={gtmAuth}
              gtmPreview={gtmPreview}
              impressionLogging={impressionLogging}
              paymentMethodsFromApi={availablePaymentMethods}
              similarOptionsCount={0}
              tripViewModel={originalTripViewModel}
              wegoAnalyticsClientId={wegoAnalyticsClientId}
              wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
              genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
              onTripCardMouseHover={onTripCardMouseHover}
              onTripViewModelSelect={onTripViewModelSelect}
            />
          )}
        </div>
      );
    },
    [
      availablePaymentMethods,
      dataDD,
      sabreFareDataDD,
      gtmAuth,
      gtmPreview,
      isFlightSearch2AndNotMulticity,
      onTripViewModelSelect,
      sortedAndFilteredTripViewModelsWithoutPlaceholder,
      translations,
      isPLSDepartLegSelection,
      isPerLegSearch,
      tripViewModelsToDisplayForNonPriceSort,
      sortedAndFilteredTripViewModelsForPLSRecommendedSortWithoutGroupHeaderPlaceholder,
      sortType,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
    ],
  );

  const groupedTripViewModelsRowRenderer = useCallback<
    ({ key, index, isVisible, style }: ListRowProps) => JSX.Element
  >(
    ({ key, index, style }: ListRowProps) => {
      const groupKey = groupedTripViewModelsDetailToShow[index].key;
      const maxLength = groupedTripViewModelsDetailToShow[index].maxLength;
      const tripIndex = groupedTripViewModelsDetailToShow[index].index;
      const tripViewModel: TripViewModel = groupedTripViewModelsDetailToShow[index].tripViewModel;

      const cardPosition = expandedGroupKeys.includes(groupKey)
        ? tripIndex > 0 && tripIndex < maxLength - 1
          ? 'middle'
          : tripIndex === 0
          ? 'first'
          : 'last'
        : '';
      const hasNoTopBorder =
        expandedGroupKeys.includes(groupKey) &&
        tripIndex > 0 &&
        groupedTripViewModelsDetailToShow[index - 1]?.key === groupKey;
      const isGroupExpanded =
        (tripIndex === 0 || tripIndex === maxLength - 1) && expandedGroupKeys.includes(groupKey)
          ? true
          : undefined;
      const similarOptionsCount = expandedGroupKeys.includes(groupKey)
        ? tripIndex === 0 || tripIndex === maxLength - 1
          ? maxLength - 1
          : 0
        : maxLength - 1;

      if (tripViewModel.isPlaceholder) {
        return (
          <div
            key={key}
            style={{ ...style, ...tripViewModel.placeholderStyle }}
            id={`placeholder-${tripViewModel.id}`}
          />
        );
      }

      const isGroupExpandedForGroupedFlight2 =
        tripIndex === maxLength - 1 && expandedGroupKeys.includes(groupKey);

      const similarOptionsCountForFlight2 = expandedGroupKeys.includes(groupKey)
        ? tripIndex === maxLength - 1
          ? maxLength - 1
          : 0
        : maxLength - 1;

      return (
        <div
          key={key}
          style={style}
          {...getFlightSearchResultDataDD({ tripViewModel, index, dataDD, sabreFareDataDD })}
          data-testid={tripViewModel.tripId}
        >
          {isFlightSearch2AndNotMulticity ? (
            <NewTripCard
              cardPosition={cardPosition}
              gtmAuth={gtmAuth}
              gtmPreview={gtmPreview}
              hasNoTopBorder={hasNoTopBorder}
              impressionLogging={impressionLogging}
              isBaggageFilterOn={isBaggageFilterOn}
              isGroupExpanded={isGroupExpandedForGroupedFlight2}
              similarOptionsCount={similarOptionsCountForFlight2}
              tripViewModel={tripViewModel}
              genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
              genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
              wegoAnalyticsClientId={wegoAnalyticsClientId}
              wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
              tripCardIndex={index}
              onSimilarOptionsClick={() => handleSimilarOptionsClick(groupKey)}
              onTripViewModelSelect={() => onTripViewModelSelect(tripViewModel)}
            />
          ) : (
            <TripCard
              cardPosition={cardPosition}
              gtmAuth={gtmAuth}
              gtmPreview={gtmPreview}
              hasNoTopBorder={hasNoTopBorder}
              impressionLogging={impressionLogging}
              isGroupExpanded={isGroupExpanded}
              paymentMethodsFromApi={availablePaymentMethods}
              similarOptionsCount={similarOptionsCount}
              tripViewModel={tripViewModel}
              wegoAnalyticsClientId={wegoAnalyticsClientId}
              wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
              genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
              onSimilarOptionsClick={() => handleSimilarOptionsClick(groupKey)}
              onTripCardMouseHover={onTripCardMouseHover}
              onTripViewModelSelect={onTripViewModelSelect}
            />
          )}
        </div>
      );
    },
    [
      availablePaymentMethods,
      dataDD,
      sabreFareDataDD,
      expandedGroupKeys,
      groupedTripViewModelDetailWithoutPlaceholder,
      groupedTripViewModelsDetailToShow,
      gtmAuth,
      gtmPreview,
      handleSimilarOptionsClick,
      isFlightSearch2AndNotMulticity,
      onTripViewModelSelect,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
    ],
  );

  const handleVisibleRowsRenderedChanged = useCallback(
    ({ startIndex, stopIndex }: { startIndex: number; stopIndex: number }) => {
      setVisibleIndexRange({ startIndex, stopIndex });
    },
    [],
  );

  // Genzo Search View Tracking for UnGrouped Trips
  useEffect(() => {
    if (visibleIndexRange && sortType !== 'price') {
      let newVisibleTripCardForUngroupedTrips: TripViewModelDataForGenzo[] = [];
      for (
        let index = visibleIndexRange.startIndex;
        index <= visibleIndexRange.stopIndex;
        index++
      ) {
        const originalTripViewModel: TripViewModel = tripViewModelsToDisplayForNonPriceSort[index];

        if (originalTripViewModel) {
          const indexWithoutPlaceholder =
            sortedAndFilteredTripViewModelsWithoutPlaceholder.findIndex(
              (trip) => trip.tripId === originalTripViewModel.tripId,
            );

          if (indexWithoutPlaceholder > -1) {
            const position = indexWithoutPlaceholder + 1;

            const fare = sortFlightFares(
              originalTripViewModel.filteredFares.map((fareViewModel) => fareViewModel.fare),
              'asc',
              true,
            )[0];

            if (!!fare) {
              newVisibleTripCardForUngroupedTrips.push({
                id: originalTripViewModel.tripId,
                position,
                score: fare.score,
                fare_id: fare.id,
                provider_code: fare.providerCode,
                amount_usd: fare.price.amountUsd,
                total_amount_usd: fare.price.totalAmountUsd,
                ...(!!originalTripViewModel.sponsored && {
                  sponsoredAdCode: originalTripViewModel.sponsored.sponsoredAdCode,
                  ad_position: originalTripViewModel.sponsored.adPosition,
                  ad_ecpc: fare.ecpc,
                }),
              });
            }
          }
        }
      }
      setVisibleTripCardForUngroupedTrips(newVisibleTripCardForUngroupedTrips);
    }
  }, [
    sortType,
    visibleIndexRange,
    tripViewModelsToDisplayForNonPriceSort,
    sortedAndFilteredTripViewModelsWithoutPlaceholder,
  ]);

  // Genzo Search View Tracking for Grouped Trips
  useEffect(() => {
    if (visibleIndexRange && sortType === 'price') {
      let newVisibleTripCardForGroupedTrips: TripViewModelDataForGenzo[] = [];
      for (
        let index = visibleIndexRange.startIndex;
        index <= visibleIndexRange.stopIndex;
        index++
      ) {
        const tripViewModel: TripViewModel =
          groupedTripViewModelsDetailToShow[index]?.tripViewModel;

        if (tripViewModel) {
          const indexWithoutPlaceholder = groupedTripViewModelDetailWithoutPlaceholder.findIndex(
            (group) =>
              (group as GroupedTripViewModel).tripViewModel.tripId === tripViewModel.tripId,
          );

          if (indexWithoutPlaceholder > -1) {
            const position = indexWithoutPlaceholder + 1;

            const fare = sortFlightFares(
              tripViewModel.filteredFares.map((fareViewModel) => fareViewModel.fare),
              'asc',
              true,
            )[0];

            if (!!fare) {
              newVisibleTripCardForGroupedTrips.push({
                id: tripViewModel.tripId,
                position,
                score: fare.score,
                fare_id: fare.id,
                provider_code: fare.providerCode,
                amount_usd: fare.price.amountUsd,
                total_amount_usd: fare.price.totalAmountUsd,
                ...(!!tripViewModel.sponsored && {
                  sponsored_ad_code: tripViewModel.sponsored.sponsoredAdCode,
                  ad_position: tripViewModel.sponsored.adPosition,
                  ad_ecpc: fare.ecpc,
                }),
              });
            }
          }
        }
      }
      setVisibleTripCardForGroupedTrips(newVisibleTripCardForGroupedTrips);
    }
  }, [
    sortType,
    visibleIndexRange,
    groupedTripViewModelsDetailToShow,
    groupedTripViewModelDetailWithoutPlaceholder,
  ]);

  /** Recompute row height when sort type or the list of trips to be displayed changes
   * Recompute row height when topLeaderboardAdsHeight, middleLeaderboardAdsHeight and bottomLeaderboardAdsHeight changes
   */
  useEffect(() => {
    if (!!listRef && !!listRef.current) {
      listRef.current.recomputeRowHeights();
    }
  }, [
    listRef,
    sortType,
    groupedTripViewModelsDetailToShow,
    tripViewModelsToDisplayForNonPriceSort,
    topLeaderboardAdsHeight,
    middleLeaderboardAdsHeight,
    bottomLeaderboardAdsHeight,
  ]);

  const ListSection = useMemo(() => {
    // New Flight Search 2.0

    if (
      (isFlightSearch2AndNotMulticity &&
        isLoading &&
        finalTripViewModelsToShowBasedOnSortType.length === 0) ||
      isPLSChangeLegLoading
    ) {
      return [1, 2, 3, 4, 5, 6, 7].map((card) => (
        <NewTripCard
          key={card}
          genzoTrackApplyFilterSearchView={() => {}}
          genzoTrackApplyFilterActionEvent={() => {}}
          isForceLoading
        />
      ));
    }

    if (isFlightSearch2AndNotMulticity) {
      if (sortType === 'price' && !isPerLegSearch) {
        return (
          <WindowScroller
            scrollElement={window}
            onScroll={() => {
              setScrollToIndex(-1);
            }}
          >
            {({
              height,
              isScrolling,
              registerChild,
              onChildScroll,
              scrollTop,
            }: WindowScrollerChildProps) => (
              <AutoSizer disableHeight>
                {({ width }: Size) => (
                  <div
                    ref={registerChild}
                    style={{
                      width: '100%',
                      height: `${totalRowHeightForNewTripCardForPriceSort}px`,
                    }}
                  >
                    <List
                      ref={listRef}
                      style={{
                        height: '100%',
                      }}
                      autoHeight
                      height={height}
                      isScrolling={isScrolling}
                      onScroll={onChildScroll}
                      overscanRowCount={5}
                      rowCount={groupedTripViewModelsDetailToShow.length}
                      rowHeight={getRowHeightForGroupedTripViewModel}
                      rowRenderer={groupedTripViewModelsRowRenderer}
                      scrollToIndex={scrollToIndex}
                      scrollTop={scrollToIndex === -1 ? scrollTop : undefined}
                      tabIndex={null}
                      width={width}
                      onRowsRendered={handleVisibleRowsRenderedChanged}
                    />
                  </div>
                )}
              </AutoSizer>
            )}
          </WindowScroller>
        );
      }

      return (
        <WindowScroller scrollElement={window}>
          {({
            height,
            isScrolling,
            registerChild,
            onChildScroll,
            scrollTop,
          }: WindowScrollerChildProps) => (
            <AutoSizer disableHeight>
              {({ width }: Size) => (
                <div
                  ref={registerChild}
                  style={{
                    width: '100%',
                    height: `${totalRowHeightForNewTripCardForNonPriceSort}px`,
                  }}
                >
                  <List
                    style={{
                      height: '100%',
                    }}
                    ref={listRef}
                    autoHeight
                    height={height}
                    isScrolling={isScrolling}
                    onScroll={onChildScroll}
                    overscanRowCount={5}
                    rowCount={tripViewModelsToDisplayForNonPriceSort.length}
                    rowHeight={getRowHeightSortedAndFilteredTripViewModel}
                    rowRenderer={sortedAndFilteredTripViewModelsRowRenderer}
                    scrollTop={scrollTop}
                    tabIndex={null}
                    width={width}
                    onRowsRendered={handleVisibleRowsRenderedChanged}
                  />
                </div>
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      );
    }

    // Old Flight Search
    if (sortType === 'price' && groupedTripViewModelsDetailToShow.length > 0) {
      return (
        <WindowScroller
          scrollElement={window}
          onScroll={() => {
            setScrollToIndex(-1);
          }}
        >
          {({
            height,
            isScrolling,
            registerChild,
            onChildScroll,
            scrollTop,
          }: WindowScrollerChildProps) => (
            <AutoSizer disableHeight>
              {({ width }: Size) => (
                <div ref={registerChild}>
                  <List
                    ref={listRef}
                    autoHeight
                    height={height}
                    isScrolling={isScrolling}
                    onScroll={onChildScroll}
                    overscanRowCount={5}
                    rowCount={groupedTripViewModelsDetailToShow.length}
                    rowHeight={getRowHeightForGroupedTripViewModel}
                    rowRenderer={groupedTripViewModelsRowRenderer}
                    scrollToIndex={scrollToIndex}
                    scrollTop={scrollToIndex === -1 ? scrollTop : undefined}
                    tabIndex={null}
                    width={width}
                    onRowsRendered={handleVisibleRowsRenderedChanged}
                  />
                </div>
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      );
    }

    if (sortType !== 'price' && tripViewModelsToDisplayForNonPriceSort.length > 0) {
      return (
        <WindowScroller scrollElement={window}>
          {({
            height,
            isScrolling,
            registerChild,
            onChildScroll,
            scrollTop,
          }: WindowScrollerChildProps) => (
            <AutoSizer disableHeight>
              {({ width }: Size) => (
                <div ref={registerChild}>
                  <List
                    ref={listRef}
                    autoHeight
                    height={height}
                    isScrolling={isScrolling}
                    onScroll={onChildScroll}
                    overscanRowCount={5}
                    rowCount={tripViewModelsToDisplayForNonPriceSort.length}
                    rowHeight={getRowHeightSortedAndFilteredTripViewModel}
                    rowRenderer={sortedAndFilteredTripViewModelsRowRenderer}
                    scrollTop={scrollTop}
                    tabIndex={null}
                    width={width}
                    onRowsRendered={handleVisibleRowsRenderedChanged}
                  />
                </div>
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      );
    }

    return null;
  }, [
    finalTripViewModelsToShowBasedOnSortType.length,
    getRowHeightForGroupedTripViewModel,
    getRowHeightSortedAndFilteredTripViewModel,
    groupedTripViewModelsDetailToShow.length,
    groupedTripViewModelsRowRenderer,
    isLoading,
    isPLSChangeLegLoading,
    isPerLegSearch,
    isFlightSearch2AndNotMulticity,
    scrollToIndex,
    sortType,
    sortedAndFilteredTripViewModelsRowRenderer,
    totalRowHeightForNewTripCardForNonPriceSort,
    totalRowHeightForNewTripCardForPriceSort,
    tripViewModelsToDisplayForNonPriceSort.length,
  ]);

  return <>{ListSection}</>;
};

export default FlightSearchResultList;
